/**
 * Onboarding Service
 * Pure business logic for onboarding status management
 * No React dependencies - fully testable
 *
 * Uses ProfileService for data access to ensure consistent
 * handling of both legacy flat format and new nested format.
 */

// Note: Direct Firestore imports removed - using ProfileService for all data access
import { loadProfile, saveOnboardingState } from '../../profile/services/profileService';
import logger from '@/shared/utils/core/logger';
import {
  trackOnboardingStatusChecked,
  trackOnboardingCompleted,
  trackOnboardingSkipped,
  trackOnboardingError
} from '@/shared/utils/analytics/analytics';


/**
 * Check if user needs to see onboarding
 * Returns comprehensive status information
 *
 * Uses ProfileService.loadProfile() to read from both collections
 * and get normalized data (handles flat and nested formats).
 */
export async function checkOnboardingStatus(user) {
  if (!user) {
    throw new Error('User is required to check onboarding status');
  }

  logger.info('🔍 [OnboardingService] Checking status for user:', user.uid);

  // Use ProfileService to get normalized profile data from both collections
  const profile = await loadProfile(user.uid);

  if (profile) {
    // Check both nested format (new) and flat format (legacy) for onboarding state
    // ProfileService.loadProfile() normalizes data to include both formats
    const onboardingCompleted = profile.onboarding?.completed || profile.onboardingCompleted;
    const onboardingSkipped = profile.onboarding?.skipped || profile.onboardingSkipped;
    const onboardingShown = profile.onboarding?.shown || profile.onboardingShown;

    // Show onboarding if ALL conditions are met:
    // 1. User has NEVER completed or skipped onboarding (explicit user actions)
    // 2. User has no birth data (genuinely new profile)
    // NOTE: onboardingShown is intentionally excluded from this gate — for email
    // signup users, shown=true gets written before they see the onboarding modal
    // (it's blocked by the email verification screen), so using it as a gate causes
    // new users to never see onboarding after a page reload or email verification.
    const hasNeverInteracted = !onboardingCompleted && !onboardingSkipped;
    const hasNoProfileData = !profile.birthDate && !profile.lastCalculated;
    const needsOnboarding = hasNeverInteracted && hasNoProfileData;

    logger.debug('✅ [OnboardingService] Decision:', {
      needsOnboarding,
      hasNeverInteracted,
      hasNoProfileData,
      reason: needsOnboarding
        ? 'User has never completed onboarding and has no profile data'
        : 'User has completed/skipped onboarding or has profile data'
    });

    // Track status check result
    trackOnboardingStatusChecked({
      needsOnboarding,
      profileExists: true,
      hasCompleted: !!onboardingCompleted,
      hasSkipped: !!onboardingSkipped,
      authMethod: profile.provider || 'unknown'
    });

    return {
      needsOnboarding,
      profileExists: true,
      profileData: profile
    };
  } else {
    // Profile doesn't exist (shouldn't happen with auto-creation)
    // Show onboarding to ensure user gets set up

    // Track status check result
    trackOnboardingStatusChecked({
      needsOnboarding: true,
      profileExists: false,
      hasCompleted: false,
      hasSkipped: false,
      authMethod: 'unknown'
    });

    return {
      needsOnboarding: true, // Always show onboarding if no profile exists
      profileExists: false,
      profileData: null
    };
  }
}

/**
 * Mark onboarding as shown to prevent re-display
 * Called BEFORE showing onboarding to prevent race conditions
 *
 * Uses ProfileService.saveOnboardingState() for dual-write to both collections.
 */
export async function markOnboardingShown(user) {
  if (!user) {
    throw new Error('User is required to mark onboarding as shown');
  }

  logger.debug('🔐 [OnboardingService] Marking onboarding as shown');

  // Use ProfileService for dual-write to both collections
  await saveOnboardingState(user.uid, {
    shown: true,
    completed: false,
    skipped: false
  });
}

/**
 * Mark onboarding as completed
 * @param {Object} user - Firebase user object
 * @param {Object} options - Additional tracking options
 *
 * Uses ProfileService.saveOnboardingState() for dual-write to both collections.
 */
export async function markOnboardingComplete(user, options = {}) {
  if (!user) {
    throw new Error('User is required to mark onboarding complete');
  }

  try {
    // Use ProfileService for dual-write to both collections
    await saveOnboardingState(user.uid, {
      completed: true,
      shown: true,
      skipped: false
    });

    // Track successful completion
    trackOnboardingCompleted({
      authMethod: options.authMethod || 'unknown',
      totalTime: options.totalTime || 0,
      stepsCompleted: options.stepsCompleted || 4,
      hasBirthDate: options.hasBirthDate || false,
      hasBirthTime: options.hasBirthTime || false,
      hasBirthPlace: options.hasBirthPlace || false
    });

    logger.debug('✅ [OnboardingService] Marked onboarding as completed');
  } catch (error) {
    trackOnboardingError({
      errorType: 'mark_complete_failed',
      errorMessage: error.message,
      step: 'completion'
    });
    throw error;
  }
}

/**
 * Mark onboarding as skipped
 * @param {Object} user - Firebase user object
 * @param {Object} options - Additional tracking options
 *
 * Uses ProfileService.saveOnboardingState() for dual-write to both collections.
 */
export async function markOnboardingSkipped(user, options = {}) {
  if (!user) {
    throw new Error('User is required to mark onboarding skipped');
  }

  try {
    // Use ProfileService for dual-write to both collections
    await saveOnboardingState(user.uid, {
      skipped: true,
      shown: true,
      completed: false
    });

    // Track skip event
    trackOnboardingSkipped({
      skippedAtStep: options.skippedAtStep || 'welcome',
      timeBeforeSkip: options.timeBeforeSkip || 0,
      authMethod: options.authMethod || 'unknown'
    });

    logger.debug('⏭️ [OnboardingService] Marked onboarding as skipped');
  } catch (error) {
    trackOnboardingError({
      errorType: 'mark_skipped_failed',
      errorMessage: error.message,
      step: options.skippedAtStep || 'unknown'
    });
    throw error;
  }
}

/**
 * Reset onboarding status (for re-showing)
 *
 * Uses ProfileService.saveOnboardingState() for dual-write to both collections.
 */
export async function resetOnboarding(user) {
  if (!user) {
    throw new Error('User is required to reset onboarding');
  }

  // Use ProfileService for dual-write to both collections
  await saveOnboardingState(user.uid, {
    completed: false,
    skipped: false,
    shown: false
  });
}
